import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MmSelect from './MmSelect';

/**
 * Formulaire paiement mobile-first (membre).
 * Flux : montant → moyen → mois → preuve → envoyer.
 */
export default function MemberPaymentFormMobile({
  localeFmt = 'fr-FR',
  title,
  isEdit = false,
  payForOther = false,
  formData,
  setFormData,
  formKind,
  setFormKind,
  exceptionalEnabled = false,
  exceptionalTypes = [],
  memberHasExceptionalType,
  formatExceptionalMonthKey,
  parseExceptionalTypeKeyFromMonth,
  isExceptionalMonthValue,
  showExceptionalFormFields = false,
  autoAllocatePreview,
  shouldAutoAllocate = false,
  monthlyContributionAmount = 0,
  lastPaidMonthLabel = '',
  lastPaidLoading = false,
  members = [],
  effectivePersonId = '',
  selfDisplayName = '',
  formPaymentOptions = [],
  defaultPaymentMethod = '',
  ledgerPmLoading = false,
  paymentMethodsEmpty = false,
  uploadedFiles = [],
  uploading = false,
  onFileUpload,
  onRemoveFile,
  onUpdateFileDescription,
  onChangePayForWhom,
  canChangePayForWhom = false,
  getMonthName,
  onCancel,
  onSubmit,
  submitLabel,
}) {
  const { t } = useTranslation('common');
  const [moreOpen, setMoreOpen] = useState(false);
  const amountRef = useRef(null);
  const fileRef = useRef(null);
  const defaultedProofsRef = useRef(new Set());

  useEffect(() => {
    const id = window.setTimeout(() => amountRef.current?.focus?.(), 180);
    return () => window.clearTimeout(id);
  }, []);

  // Préremplir la description preuve une seule fois par fichier
  useEffect(() => {
    if (!uploadedFiles?.length || typeof onUpdateFileDescription !== 'function') return;
    const defaultDesc = t('contributionsPage.mobileForm.defaultProofDesc');
    for (const fileItem of uploadedFiles) {
      if (typeof fileItem === 'string') continue;
      const url = fileItem.file_url;
      if (!url || defaultedProofsRef.current.has(url)) continue;
      if (!String(fileItem.description || '').trim()) {
        defaultedProofsRef.current.add(url);
        onUpdateFileDescription(url, defaultDesc);
      } else {
        defaultedProofsRef.current.add(url);
      }
    }
  }, [uploadedFiles, onUpdateFileDescription, t]);

  const memberOptions = useMemo(
    () =>
      (members || [])
        .filter((m) => String(m?.id || '').trim() !== String(effectivePersonId || '').trim())
        .map((member) => ({
          value: String(member.id),
          label: String(member.name || member.id || '').trim() || String(member.id),
          hint: [member.email, member.phone].filter(Boolean).join(' · '),
        })),
    [members, effectivePersonId]
  );

  const exceptionalOpts =
    exceptionalEnabled && Array.isArray(exceptionalTypes)
      ? exceptionalTypes.filter((exT) =>
          payForOther || typeof memberHasExceptionalType !== 'function'
            ? true
            : !memberHasExceptionalType(exT.key)
        )
      : [];

  const monthly = Number(monthlyContributionAmount) || 0;
  const quickAmounts =
    monthly > 0
      ? [
          { label: '1×', value: monthly },
          { label: '2×', value: monthly * 2 },
          { label: '3×', value: monthly * 3 },
        ]
      : [];

  const amountValue = Number(formData?.amount) || 0;
  const methodValue = formData.payment_method || defaultPaymentMethod;

  const setAmount = (value) => {
    setFormData({ ...formData, amount: value });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mm-modal-header">
        <div className="min-w-0">
          <p className="mm-hero__label text-[#1f6ab8]">{t('contributionsPage.pageTitle', 'Cotisations')}</p>
          <h2 className="mt-0.5 truncate font-display text-lg font-extrabold tracking-tight text-[#0f3350]">{title}</h2>
          {!payForOther && !isEdit ? (
            <p className="mt-1 truncate text-xs text-slate-500">
              {selfDisplayName || t('contributionsPage.form.you')}
              {canChangePayForWhom ? (
                <>
                  {' · '}
                  <button
                    type="button"
                    onClick={onChangePayForWhom}
                    className="font-semibold text-[#2f6fd4]"
                  >
                    {t('contributionsPage.payForWhom.change')}
                  </button>
                </>
              ) : null}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label={t('contributionsPage.form.closeAria')}
          className="mm-press flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100/80 text-slate-500"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {exceptionalEnabled && !isEdit ? (
            <div className="grid grid-cols-2 gap-1 rounded-xl bg-white p-1 ring-1 ring-slate-200">
              <button
                type="button"
                onClick={() => {
                  setFormKind('monthly');
                  setFormData((prev) => ({
                    ...prev,
                    month: isExceptionalMonthValue?.(prev.month) ? '' : prev.month,
                  }));
                }}
                className={`min-h-[40px] rounded-lg text-xs font-bold ${
                  formKind === 'monthly'
                    ? 'bg-gradient-to-br from-[#1aa6b8] to-[#113a5b] text-white'
                    : 'text-slate-600'
                }`}
              >
                {t('contributionsPage.mobileForm.monthly')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormKind('exceptional');
                  const pick =
                    exceptionalOpts.find((exT) => !memberHasExceptionalType?.(exT.key)) ||
                    exceptionalOpts[0];
                  if (pick) {
                    setFormData((prev) => ({
                      ...prev,
                      month: formatExceptionalMonthKey(pick.key),
                    }));
                  }
                }}
                className={`min-h-[40px] rounded-lg text-xs font-bold ${
                  formKind === 'exceptional'
                    ? 'bg-gradient-to-br from-[#1aa6b8] to-[#113a5b] text-white'
                    : 'text-slate-600'
                }`}
              >
                {t('contributionsPage.mobileForm.exceptional')}
              </button>
            </div>
          ) : null}

          {payForOther && !isEdit ? (
            <div className="rounded-2xl bg-white/90 p-3 ring-1 ring-white/70 shadow-[0_8px_24px_-16px_rgba(15,51,80,0.22)] backdrop-blur-sm">
              <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">
                {t('contributionsPage.form.member')}
              </label>
              <MmSelect
                value={formData.person_id || ''}
                onChange={(personId) =>
                  setFormData({
                    ...formData,
                    person_id: personId,
                    member_id: personId,
                  })
                }
                required
                searchable
                placeholder={t('contributionsPage.form.selectMember')}
                searchPlaceholder={t('contributionsPage.form.searchMember', {
                  defaultValue: 'Rechercher un membre…',
                })}
                options={memberOptions}
              />
              {canChangePayForWhom ? (
                <button
                  type="button"
                  onClick={onChangePayForWhom}
                  className="mt-2 text-xs font-semibold text-[#2f6fd4]"
                >
                  {t('contributionsPage.payForWhom.change')}
                </button>
              ) : null}
            </div>
          ) : null}

          {/* Montant */}
          <div className="rounded-2xl bg-white/90 p-3 ring-1 ring-white/70 shadow-[0_8px_24px_-16px_rgba(15,51,80,0.22)] backdrop-blur-sm">
            <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {t('contributionsPage.mobileForm.amount')}
            </label>
            <div className="relative">
              <input
                ref={amountRef}
                type="number"
                inputMode="decimal"
                value={formData.amount}
                onChange={(e) => setAmount(parseFloat(e.target.value))}
                className="w-full min-h-[56px] rounded-xl border border-slate-200 bg-slate-50/50 px-3 pr-14 text-2xl font-bold tabular-nums text-[#0f3350] focus:border-[#2f6fd4] focus:outline-none focus:ring-2 focus:ring-[#2f6fd4]/25"
                required
                min="0"
                step={monthly > 0 ? monthly : undefined}
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm font-semibold text-slate-400">
                FBU
              </span>
            </div>
            {monthly > 0 && !showExceptionalFormFields ? (
              <p className="mt-1.5 text-[11px] text-slate-500">
                {t('contributionsPage.form.amountMultipleHint', {
                  monthly: monthly.toLocaleString(localeFmt),
                })}
              </p>
            ) : null}
            {quickAmounts.length > 0 && !isEdit ? (
              <div className="mt-2.5 flex gap-2">
                {quickAmounts.map((q) => {
                  const active = amountValue === q.value;
                  return (
                    <button
                      key={q.label}
                      type="button"
                      onClick={() => setAmount(q.value)}
                      className={`min-h-[36px] flex-1 rounded-xl text-xs font-bold tabular-nums ${
                        active
                          ? 'mm-cta min-h-[36px]'
                          : 'bg-slate-100/90 text-slate-700 active:bg-slate-200'
                      }`}
                    >
                      {q.label}
                      <span className="mt-0.5 block text-[10px] font-medium opacity-80">
                        {q.value.toLocaleString(localeFmt)}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          {/* Moyen */}
          <div className="rounded-2xl bg-white/90 p-3 ring-1 ring-white/70 shadow-[0_8px_24px_-16px_rgba(15,51,80,0.22)] backdrop-blur-sm">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {t('contributionsPage.mobileForm.method')}
            </p>
            {formPaymentOptions.length > 0 && formPaymentOptions.length <= 6 ? (
              <div className="flex flex-wrap gap-2">
                {formPaymentOptions.map((opt) => {
                  const active = methodValue === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, payment_method: opt.value })}
                      className={`min-h-[40px] rounded-xl px-3 text-xs font-bold ${
                        active
                          ? 'mm-cta min-h-[40px]'
                          : 'bg-slate-100/90 text-slate-700 active:bg-slate-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            ) : (
              <MmSelect
                value={methodValue}
                onChange={(payment_method) => setFormData({ ...formData, payment_method })}
                required
                disabled={formPaymentOptions.length === 0}
                placeholder={t('contributionsPage.mobileForm.method')}
                options={formPaymentOptions.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
              />
            )}
            {ledgerPmLoading ? (
              <p className="mt-1.5 text-[11px] text-slate-500">{t('contributionsPage.form.pmLoadingHint')}</p>
            ) : null}
            {paymentMethodsEmpty ? (
              <p className="mt-1.5 text-[11px] text-amber-700">{t('contributionsPage.mobileForm.pmNone')}</p>
            ) : null}
          </div>

          {/* Mois (optionnel) — vide ⇒ répartition auto */}
          {!showExceptionalFormFields ? (
            <div className="rounded-2xl bg-white/90 p-3 ring-1 ring-white/70 shadow-[0_8px_24px_-16px_rgba(15,51,80,0.22)] backdrop-blur-sm space-y-2">
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {t('contributionsPage.mobileForm.month')}
                  {!isEdit ? (
                    <span className="ml-1 font-normal normal-case tracking-normal text-slate-400">
                      {t('contributionsPage.form.startMonthOptional')}
                    </span>
                  ) : null}
                </label>
                {isEdit ? (
                  <div className="flex min-h-[48px] items-center rounded-xl bg-slate-50 px-3 text-base font-semibold capitalize text-slate-800 ring-1 ring-slate-200">
                    {getMonthName?.(formData.month) || formData.month || '—'}
                  </div>
                ) : (
                  <input
                    type="month"
                    value={
                      /^\d{4}-\d{2}$/.test(String(formData.month || '').trim().slice(0, 7))
                        ? String(formData.month).trim().slice(0, 7)
                        : ''
                    }
                    onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                    className="w-full min-h-[48px] rounded-xl border border-slate-200 px-3 text-base"
                  />
                )}
                {!isEdit ? (
                  <>
                    <p className="mt-1.5 text-[11px] text-slate-500">
                      {shouldAutoAllocate
                        ? t('contributionsPage.form.monthEmptyAutoHint', {
                            monthly: Number(monthlyContributionAmount || 0).toLocaleString(localeFmt),
                          })
                        : t('contributionsPage.form.monthSelectedHint')}
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      {t('contributionsPage.mobileForm.lastPaid')}{' '}
                      <span className="font-medium text-slate-700">
                        {lastPaidLoading
                          ? '…'
                          : lastPaidMonthLabel || t('contributionsPage.mobileForm.noLastPaid')}
                      </span>
                    </p>
                    {shouldAutoAllocate &&
                    autoAllocatePreview?.enabled &&
                    autoAllocatePreview.rows?.length > 0 ? (
                      <p className="mt-1.5 text-[11px] tabular-nums text-slate-600">
                        {t('contributionsPage.mobileForm.autoPreview', {
                          count: autoAllocatePreview.rows.length,
                          start:
                            getMonthName?.(autoAllocatePreview.startMonth) ||
                            autoAllocatePreview.startMonth,
                          monthly: Number(
                            monthlyContributionAmount || autoAllocatePreview.monthly || 0
                          ).toLocaleString(localeFmt),
                        })}
                      </p>
                    ) : null}
                  </>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl bg-white/90 p-3 ring-1 ring-white/70 shadow-[0_8px_24px_-16px_rgba(15,51,80,0.22)] backdrop-blur-sm">
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {t('contributionsPage.form.period')}
              </label>
              {exceptionalOpts.length > 1 && !isEdit ? (
                <MmSelect
                  value={parseExceptionalTypeKeyFromMonth?.(formData.month) || ''}
                  onChange={(k) => {
                    if (k) {
                      setFormData((prev) => ({
                        ...prev,
                        month: formatExceptionalMonthKey(k),
                      }));
                    }
                  }}
                  required
                  placeholder={t('contributionsPage.form.period')}
                  options={exceptionalOpts.map((exT) => ({
                    value: exT.key,
                    label: exT.label,
                  }))}
                />
              ) : (
                <div className="flex min-h-[48px] items-center rounded-xl bg-slate-50 px-3 text-base font-semibold text-slate-800 ring-1 ring-slate-200">
                  {getMonthName?.(formData.month) || formData.month}
                </div>
              )}
            </div>
          )}

          {/* Preuve */}
          <div className="rounded-2xl bg-white/90 p-3 ring-1 ring-white/70 shadow-[0_8px_24px_-16px_rgba(15,51,80,0.22)] backdrop-blur-sm">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {t('contributionsPage.mobileForm.proof')}
            </p>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx"
              onChange={onFileUpload}
              disabled={uploading}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex min-h-[52px] w-full flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 text-center active:bg-slate-100 disabled:opacity-60"
            >
              <span className="text-sm font-semibold text-[#113a5b]">
                {uploading
                  ? t('contributionsPage.form.uploading')
                  : t('contributionsPage.mobileForm.addProof')}
              </span>
              <span className="mt-0.5 text-[11px] text-slate-500">
                {t('contributionsPage.mobileForm.proofHint')}
              </span>
            </button>
            {uploadedFiles.length > 0 ? (
              <ul className="mt-2.5 space-y-2">
                {uploadedFiles.map((fileItem) => {
                  const fileUrl = typeof fileItem === 'string' ? fileItem : fileItem.file_url;
                  const fileName = String(fileUrl || '').split('/').pop();
                  const description = typeof fileItem === 'string' ? '' : fileItem.description || '';
                  return (
                    <li key={fileUrl} className="rounded-xl bg-slate-50 p-2.5 ring-1 ring-slate-200">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-xs font-medium text-slate-700">{fileName}</span>
                        <button
                          type="button"
                          onClick={() => onRemoveFile?.(fileUrl)}
                          className="shrink-0 text-xs font-semibold text-red-600"
                        >
                          {t('contributionsPage.mobileForm.remove')}
                        </button>
                      </div>
                      <input
                        type="text"
                        value={description}
                        onChange={(e) => onUpdateFileDescription?.(fileUrl, e.target.value)}
                        placeholder={t('contributionsPage.mobileForm.descPlaceholder')}
                        className="mt-1.5 w-full min-h-[36px] rounded-lg border border-slate-200 bg-white px-2.5 text-xs"
                        required
                      />
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            className="px-1 text-xs font-semibold text-slate-500"
          >
            {moreOpen
              ? t('contributionsPage.mobileForm.hideMore')
              : t('contributionsPage.mobileForm.showMore')}
          </button>
          {moreOpen ? (
            <textarea
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full min-h-[72px] rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm"
              placeholder={t('contributionsPage.form.notes')}
              rows={2}
            />
          ) : null}
        </div>

        <div className="mm-modal-footer flex-col !gap-1.5">
          <button
            type="submit"
            className="mm-cta flex w-full gap-2 text-sm"
          >
            <span>{submitLabel}</span>
            {amountValue > 0 ? (
              <span className="tabular-nums opacity-90">
                · {amountValue.toLocaleString(localeFmt)} FBU
              </span>
            ) : null}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex min-h-[40px] w-full items-center justify-center text-sm font-medium text-slate-500"
          >
            {t('contributionsPage.form.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}
